import { useState } from 'react'
import { ErroreApi } from '../../servizi/api.ts'
import * as posta from '../../servizi/posta.ts'
import * as bozze from '../../servizi/bozze.ts'
import { Bottone, Etichetta } from '../../componenti/base.tsx'
import { IconaGruppo } from '../../componenti/Icone.tsx'

/**
 * Le bozze del gestionale nella cartella «Bozze» della casella (3/11/2026). Gli utenti, abituati a Roundcube, le
 * cercavano li' e non le trovavano: stavano solo nella colonna, sotto «Bozze in scrittura». Qui stanno in cima
 * all'elenco della cartella, prima dei messaggi che la cartella ha gia' (le bozze lette dal server, i messaggi
 * «Non inviato»); un clic le riapre nel compositore. Una bozza con un invio non riuscito non si ripete: nelle
 * «Bozze» e' il suo messaggio «Non inviato» (servizi/bozze-condivise.ts dell'API, elencoBozze).
 */
export function BozzeNellaCartella({ voci, apri }: { voci: bozze.VoceBozza[]; apri: (id: string) => void }) {
  return (
    <>
      {voci.map((b) => (
        <div key={b.id} className="posta-voce-riga posta-voce-bozza">
          <button type="button" className="posta-voce" onClick={() => apri(b.id)}
                  title="Apri la bozza nel compositore">
            <span className="capo">
              <span className="mittenti">{b.a ? `a ${b.a}` : 'nessun destinatario'}</span>
              <span className="quando">{posta.dataElenco(b.modificata_il)}</span>
            </span>
            <span className="oggetto">{b.oggetto || '(senza oggetto)'}</span>
            <span className="coda">
              <Etichetta tono="accento">bozza</Etichetta>
              {b.risposta && <Etichetta>risposta</Etichetta>}
              {b.stato === 'in_invio' && <Etichetta tono="accento">in invio</Etichetta>}
              {b.creata_da && <span className="anteprima">di {b.creata_da.nome} {b.creata_da.cognome}</span>}
            </span>
            {b.presenti.length > 0 && (
              <span className="anteprima"><IconaGruppo /> scrive: {bozze.descriviPresenti(b.presenti)}</span>
            )}
          </button>
        </div>
      ))}
    </>
  )
}

/** «Non inviato» in rosso, sulla riga dell'elenco: il messaggio non e' partito (3/11/2026). */
export function EtichettaNonInviato({ errore }: { errore?: string | null }) {
  return (
    <Etichetta tono="critico">
      <span title={errore ? `Non inviato: ${posta.motivoNonInviato(errore)}` : 'Non inviato'}>Non inviato</span>
    </Etichetta>
  )
}

/**
 * Sopra un messaggio non partito (3/11/2026): «Non inviato», il motivo, «Riprova» e «Modifica». «Riprova» lo
 * rispedisce com'e', con lo stesso Message-ID; «Modifica» lo riapre nel compositore. Senza i comandi dove non si
 * spedisce (casella spenta, sola lettura, supervisione): li', dice perche'.
 */
export function NonInviato({ messaggio, comandi, perche }: {
  messaggio: posta.Messaggio
  /** `riprova` nullo sulla casella spenta: da li' non parte, si riapre con «Modifica» e si cambia «Da». */
  comandi: { riprova: (() => Promise<string | null | 'inviato'>) | null; modifica: () => Promise<void> } | null
  /** Perche' da qui non si rispedisce, quando non ci sono i comandi. */
  perche?: string | null
}) {
  const [inCorso, setInCorso] = useState<'riprova' | 'modifica' | null>(null)
  const [esito, setEsito] = useState<string | null>(null)

  async function esegui(quale: 'riprova' | 'modifica') {
    if (!comandi) return
    setInCorso(quale); setEsito(null)
    try {
      if (quale === 'modifica') { await comandi.modifica(); return }
      if (!comandi.riprova) return
      const r = await comandi.riprova()
      if (r !== 'inviato') setEsito(`Ancora non inviato: ${posta.motivoNonInviato(r)}.`)
    } catch (e) {
      setEsito(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.')
    } finally { setInCorso(null) }
  }

  return (
    <div className="posta-non-inviato" role="alert">
      <div>
        <strong>Non inviato</strong>: {posta.motivoNonInviato(messaggio.errore)}. Il messaggio non è partito e sta
        nelle «Bozze» della casella.
        {!comandi && perche && <> {perche}</>}
        {esito && <div className="posta-non-inviato-esito">{esito}</div>}
      </div>
      {comandi && (
        <div className="riga">
          {comandi.riprova && (
            <Bottone dimensione="piccolo" variante="primario" inCorso={inCorso === 'riprova'} disabled={inCorso !== null}
                     onClick={() => void esegui('riprova')}>Riprova</Bottone>
          )}
          <Bottone dimensione="piccolo" variante="secondario" inCorso={inCorso === 'modifica'} disabled={inCorso !== null}
                   onClick={() => void esegui('modifica')}>Modifica</Bottone>
        </div>
      )}
    </div>
  )
}
